import React, {
  DragEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  ButtonToolbar,
  Classes,
  Icon,
  KalendarIntentStyle,
  Menu,
  MenuDivider,
  MenuItem,
} from '../index';
import {
  addDays,
  areIntervalsOverlapping,
  differenceInMilliseconds,
  endOfDay,
  format,
  isAfter,
  isSameDay,
  isSameWeek,
  isSaturday,
  isSunday,
  isWithinInterval,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { cs } from 'date-fns/locale';
import {
  mcDay,
  mcNavigateNext,
  mcNavigatePrev,
  mcSettings,
  mcWeek,
} from '@mc/react-ui-icons';
import {
  CalendarDaysInterval,
  CalendarTimeInterval,
  getIntervals,
  getMonthYear,
} from './utils';
import { CalendarFixedPlusButton } from './CalendarFixedPlusButton';
import classNames from 'classnames';
import { CalendarEvent } from './CalendarEvent';
import {
  CALENDAR_ITEM_HEIGHTS,
  EVENT_VERTICAL_MARGIN,
  HOUR_COLUMN_ITEM_LINE_HEIGHT,
  MOUSE_MOVE_VECTOR,
  RIGHT_COLUMN_SPACE,
} from './consts';
import { FullDayEvent } from './FullDayEvent';

function calculateLeftPosition(
  column: number,
  clientWidth: number | undefined,
  columnsNumber: number,
  hoursListColumnWidth: number
): number {
  return column * ((clientWidth || 0) / columnsNumber) + hoursListColumnWidth;
}

const calculatePosition = (
  colIndex: number,
  itemIndex: number,
  itemPartIndex: number,
  itemRowsCount: number
): EventPosition => {
  const row = itemIndex * itemRowsCount + itemPartIndex;
  return { columnIndex: colIndex, rowIndex: row };
};

export type CalendarProps<ID> = {
  /**
   * Pole dnů, které se zobrazí v kalendáři.
   */
  days: Date[];
  /**
   * Pokud je uvedeno, tak se zobrazují ovládací prvky pro změnu zobrazení na aktuální den nebo týden nebo změna dne/týdne (na následující/předchozí).
   * @param days
   */
  setDays?: (days: Date[]) => void;
  /**
   * Locales pro kalendář, výchozí hodnota cs.
   */
  locale?: Locale;
  /**
   * Informace o změně pohledu na jiný týden.
   * @param weekDays seznam dnů pro změněný týden - tento týden je nově označený
   */
  onWeekChange?: (weekDays: Date[]) => void;
  /**
   * Události pro zobrazení v kalendáři.
   */
  calendarEvents?: CalendarEventProps<ID>[];
  /**
   * Založení nového eventu nebo naklonování existujícího eventu. Vpřípadě klonování jsou v novém eventu jsou překopírovaná data z sourceEvent, ale id je odnastavené.
   * @param event nový event
   */
  onCreateEvent?: (event: EventProps<ID>, sourceEventId?: string) => void;
  /**
   * Akce editace eventu - uživatel chce editovat obsah eventu - tedy aplikace typicky zobrazí formulář pro editaci.
   * @param event
   */
  onEditEvent?: (event: EventProps<ID>) => void;
  /**
   * Smazání eventu.
   * @param event
   */
  onDeleteEvent?: (event: EventProps<ID>) => void;
  /**
   * Akce při přesunu (změna start) nebo změně velikosti (konec - začátek).
   * @param event
   */
  onChangeEvent?: (event: EventProps<ID>) => void;
  /**
   * Rendrovací metoda pro dodatečné prvky headeru každého dne - bude umístěno v hlavičce dne napravo.
   * @param dayheaderProps
   */
  dayHeaderExtra?: (dayheaderProps: DayheaderProps) => ReactNode;
  /**
   * Vlastní tooltip eventu
   * @param event
   */
  customEventTooltip?: (event?: EventProps<ID>) => ReactNode;
  /**
   * Základní nastavení kalendáře
   */
  viewConfig?: CalendarViewConfig;
  onViewConfigChange?: (viewConfig: CalendarViewConfig) => void;
};

export type CalendarViewConfig = {
  timeInterval?: CalendarTimeInterval;
  daysInterval?: CalendarDaysInterval;
};

export type DayheaderProps = {
  date: Date;
};

type OverlappingStatus = { overLappedIndex: number; overlappingDates: number };

export type EventProps<ID> = {
  id?: ID;
  startDate: Date;
  endDate: Date;
  title?: string;
  intent?: KalendarIntentStyle;
  description?: string;
  fullDay?: boolean;
  disabled?: boolean;
};

/**
 * Události pro jeden pojmenovaný kalendář.
 */
export type CalendarEventProps<ID> = {
  events: EventProps<ID>[];
  /**
   * Název kalendáře.
   */
  name: string;
};

export type EventSimpleProps<ID> = {
  id?: ID;
  /**
   * Index sloupce pro aktuální zobrazení 0-n.
   */
  columnIndex: number;
  /**
   * Pixel posun od horního okraje kalendáře (od času 00:00).
   */
  topOffset: number;
  /**
   * Výška eventu v pixelech.
   */
  height: number;
  title?: string;
  description?: string;
  overlappingStatus?: OverlappingStatus;
  /**
   * Při zakládání nového eventu je toto pole prázdné, při puštění se sem dá hodnota a vytvoří se nový event (pak se volá on change na events).
   */
  dateRange?: [Date, Date];
  /**
   * Index kalendáře, do kterého tento event patří, dle tohoto indexu se např. určuje barva kalendáře apod.
   */
  calendarIndex: number;
  orignalEvent?: EventProps<ID>;
};

/**
 * Pouze přetahování eventu.
 */
type DragEventData<ID> = {
  id: ID;
  /**
   * True pokud se jedná o klonování eventu.
   */
  clone: boolean;
  /**
   * Výška eventu v pixelech.
   */
  height: number;
  /**
   * Index řádku v eventu na který bylo kliknuto (na kolikátý řádek v enventu jsem v rámci eventu kliknul).
   */
  cursorIndex: number;
  /**
   * Index sloupce XXXXXXXXXXXXXXXXXXXXXX.
   */
  columnIndex: number;
  /**
   * Index řádku začátku eventu.
   */
  topIndex: number;
  /**
   * "Výška" v milisekundách.
   */
  timeLength: number;
  currentDraggingEvent: EventSimpleProps<ID>;
};

type TimeLineProps = {
  /**
   * Šířka časové osy v pixelech - až do aktuálního dne, pokud se zobrazuje, jinak je zde šířka celé oblasti - všechny sloupce.
   */
  width: number;
  /**
   * Pixel šířka sloupce aktuálního dne.
   */
  currentDayWidth: number;
  /**
   * Pixel posun od levého okraje aktuálního dne.
   */
  currentDayLeft: number;
  /**
   * Jestli je součástí zobraní aktuální den, pokud je false, tak currentDayWidth je 0.
   */
  showCurrentDay: boolean;
};

type EventPosition = {
  /**
   * Index sloupce.
   */
  columnIndex: number;
  /**
   * Row index řádku.
   */
  rowIndex: number;
};

export const Calendar = <ID extends unknown = {}>({
  days,
  setDays,
  locale = cs,
  calendarEvents,
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  onChangeEvent,
  onWeekChange,
  dayHeaderExtra,
  customEventTooltip,
  viewConfig,
  onViewConfigChange,
}: CalendarProps<ID>) => {
  const realDays: Date[] = useMemo(() => {
    return days?.length > 0 ? days : [new Date()];
  }, [days]);

  const events = useMemo(() => {
    return (
      calendarEvents?.map((caldendarEvents) => caldendarEvents.events).flat() ||
      []
    );
  }, [calendarEvents]);
  const itemMinutes = 60;
  const columnBorder = 1; // Ach jo :(
  const columnsCount = realDays.length;
  const hours = 24;
  const hoursListColumnWidth = 42;
  const [selectedTimeInterval, setSelectedTimeInterval] = useState(
    viewConfig?.timeInterval ?? CalendarTimeInterval.FIFTEEN
  );
  const selectedTimeIntervalRef = useRef(selectedTimeInterval);
  selectedTimeIntervalRef.current = selectedTimeInterval;

  const computeTimeLineTopOffset = () => {
    const oneMinuteHeight =
      CALENDAR_ITEM_HEIGHTS[selectedTimeIntervalRef.current] / 60;
    const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes();
    const topOffset = currentMinutes * oneMinuteHeight;
    return topOffset;
  };

  //@ts-ignore
  // zatím ts-ignore protože selectedEvent nemá ted využití, odmazat po implementaci
  const [selectedEvent, setSelectedEvent] = useState<EventProps | null>(null);
  // Pokud je nastavený, tak se jedná o přesun eventu chycením myši a přesouváním na jinou pozici
  const [moveEventData, setMoveEventData] =
    useState<DragEventData<ID> | null>(null);
  const timeIntervals = getIntervals();

  // Počet řádek v jedné item - např. když áme pohled 30 minut, tak item je hodina a má 2 rows, tedy itemRowsCount bude 2, pro 60 minut bude 1, pro 15 minut bude 4
  const itemRowsCount = useMemo(() => {
    return itemMinutes / selectedTimeInterval;
  }, [selectedTimeInterval]);

  const calendarItemHeight = CALENDAR_ITEM_HEIGHTS[selectedTimeInterval];
  const itemPartHeight = calendarItemHeight / itemRowsCount;
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const colsArray = Array.from({ length: columnsCount }, (_, i) => i);
  const rowsArray = Array.from({ length: itemRowsCount }, (_, i) => i);
  const hoursArray = Array.from({ length: hours }, (_, i) => i);

  const getBodyClientWidth = () => {
    return bodyRef.current ? bodyRef.current.clientWidth : 0;
  };
  const bodyClientWidth = getBodyClientWidth();

  const draggingRef = useRef<boolean>(false);
  const resizingRef = useRef<boolean>(false);
  const creatingRef =
    useRef<{ clientX: number; clientY: number } | undefined>(undefined);

  /**
   * Šířka sloupce s obsahem pro eventy v pixelech.
   */
  const columnContentWidth =
    (bodyClientWidth - hoursListColumnWidth) / columnsCount -
    RIGHT_COLUMN_SPACE;

  const [timeLineData, setTimeLineData] = useState<TimeLineProps>({
    width: 0,
    currentDayLeft: 0,
    currentDayWidth: 0,
    showCurrentDay: false,
  });
  const [controlState, setControlState] = useState<
    CalendarDaysInterval | undefined
  >(viewConfig?.daysInterval);

  useEffect(() => {
    if (onViewConfigChange) {
      onViewConfigChange({
        daysInterval: controlState,
        timeInterval: selectedTimeInterval,
      });
    }
  }, [selectedTimeInterval, controlState]);

  const showDate: string = useMemo(() => {
    const today = new Date();
    let tempDate: Date;
    if (controlState) {
      if (realDays.find((day) => isSameDay(day, today))) {
        tempDate = today;
      } else {
        tempDate = realDays[0];
      }
    } else {
      tempDate = today;
    }

    const dateString = getMonthYear(
      tempDate.getMonth(),
      tempDate.getFullYear(),
      locale
    );
    return dateString[0].toLocaleUpperCase() + dateString.slice(1);
  }, [realDays, controlState, locale]);

  useEffect(() => {
    if (realDays.length === 1) {
      setControlState(CalendarDaysInterval.DAY);
    } else if (realDays.length === 7) {
      const [firstDate, ...rest] = realDays;
      const sameWeek = rest.every((day) => {
        return isSameWeek(firstDate, day, { weekStartsOn: 1 });
      });
      if (sameWeek) {
        setControlState(CalendarDaysInterval.WEEK);
      }
    } else {
      setControlState(undefined);
    }
  }, [realDays]);

  const [timeLineTopOffset, setTimeLineTopOffset] = useState(
    computeTimeLineTopOffset()
  );

  useLayoutEffect(() => {
    const updateTimeLineData = () => {
      const currentDayIndex = realDays.findIndex((day) =>
        isSameDay(day, new Date())
      );
      const columnWidth =
        (getBodyClientWidth() - hoursListColumnWidth) / columnsCount;
      let width;
      if (currentDayIndex >= 0) {
        width = columnWidth * currentDayIndex;
      } else {
        width = columnWidth * realDays.length;
      }

      setTimeLineData({
        width,
        currentDayWidth: columnWidth,
        currentDayLeft: currentDayIndex * columnWidth,
        showCurrentDay: currentDayIndex >= 0,
      });
    };

    setTimeLineTopOffset(computeTimeLineTopOffset());

    updateTimeLineData();
    window.addEventListener('resize', updateTimeLineData);

    return () => {
      window.removeEventListener('resize', updateTimeLineData);
    };
  }, [realDays, calendarItemHeight, bodyRef.current, selectedTimeInterval]);

  setInterval(() => {
    setTimeLineTopOffset(computeTimeLineTopOffset());
  }, 30 * 1000);

  useLayoutEffect(() => {
    if (bodyRef.current) {
      //autoscroll podle aktuálního času - jen při prvním zobrazení
      if (timeLineTopOffset > 0) {
        setTimeout(() => {
          // @ts-ignore
          bodyRef.current.scrollTop = timeLineTopOffset - 100;
        });
      }
    }
  }, []);

  const fullDayEvents: EventProps<ID>[] = useMemo(() => {
    return events?.filter((event) => event.fullDay) || [];
  }, [events, realDays]);

  const simpleEvents = useMemo(() => {
    const eventsInRange = events?.filter(
      (event) =>
        !event.fullDay &&
        realDays.some((day) => isSameDay(event.startDate, day))
    );
    //split eventtInRange into array of arrays by same start date
    const eventsByDate = eventsInRange?.reduce((acc, event) => {
      const index = acc.findIndex((item) =>
        isSameDay(item[0].startDate, event.startDate)
      );
      if (index === -1) {
        acc.push([event]);
      } else {
        acc[index].push(event);
      }
      return acc;
    }, [] as EventProps<ID>[][]);
    // Initialize with empty arrays
    let sortedEventsByDate: EventProps<ID>[][] = new Array(realDays.length)
      .fill(null)
      .map(() => []);

    eventsByDate?.forEach((eventsOnSameDay) => {
      const eventDate = eventsOnSameDay[0].startDate; // Get the date of the first event of the day
      const dayIndex = realDays.findIndex((day) => isSameDay(day, eventDate)); // Find the corresponding index in the days array

      if (dayIndex !== -1) {
        // If the day is found in the days array, place the events on that day in the sortedEventsByDate array
        sortedEventsByDate[dayIndex] = eventsOnSameDay;
      }
    });

    //convert sortedEventsByDate to simpleEvents
    const tempSipleEvents: EventSimpleProps<ID>[][] = sortedEventsByDate.map(
      (eventsOnDay, columnIndex) => {
        return eventsOnDay.map((event) => {
          const startHours = event.startDate.getHours();
          const startMinutes = event.startDate.getMinutes();
          const overlappingStatus =
            countOverlappingDatesFc(event, eventsInRange || []) || 1;
          const topOffset =
            (startHours + startMinutes / 60) * calendarItemHeight;
          const diffBetweenStartAndEnd = differenceInMilliseconds(
            event.endDate,
            event.startDate
          );
          const diffInHours = diffBetweenStartAndEnd / (1000 * 60 * 60);

          const height = diffInHours * calendarItemHeight || 1;
          const calendarIndex =
            calendarEvents?.findIndex((e) =>
              e.events.some((ev) => ev.id === event.id)
            ) || 0;
          return {
            topOffset,
            height,
            columnIndex: columnIndex,
            dateRange: [event.startDate, event.endDate],
            title: event.title,
            description: event.description,
            id: event.id,
            overlappingStatus: overlappingStatus,
            calendarIndex: calendarIndex,
            orignalEvent: event,
          };
        });
      }
    );
    return tempSipleEvents;
  }, [events, realDays, calendarItemHeight]);

  const [draggingPosition, setDraggingPosition] = useState<EventPosition>();
  const [resisingEvent, setResisingEvent] =
    useState<{ event: EventSimpleProps<ID>; top: boolean }>();
  const [startPosition, setStartPosition] =
    useState<EventPosition | undefined>(undefined);

  useEffect(() => {
    if (resisingEvent?.event) {
      document.body.style.cursor = 'row-resize';
    } else {
      document.body.style.cursor = 'default';
    }

    // Clean up by resetting the cursor style when the component unmounts
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [resisingEvent]);

  const scrollSpeedRef = useRef<{ y: number }>({ y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (resizingRef.current || creatingRef.current || draggingRef.current) {
        if (bodyRef.current) {
          const { clientY } = event;
          const { top, bottom } = bodyRef.current.getBoundingClientRect();

          // Calculate distances from the edges
          const distanceToTop = clientY - top;
          const distanceToBottom = bottom - clientY;

          const edgeThreshold = 50; // Adjust this value as needed
          const maxSpeed = 20; // Maximum scroll speed

          let newScrollSpeedY = 0;

          // Calculate vertical scroll speed
          if (distanceToTop < edgeThreshold) {
            newScrollSpeedY =
              -((edgeThreshold - distanceToTop) / edgeThreshold) * maxSpeed;
          } else if (distanceToBottom < edgeThreshold) {
            newScrollSpeedY =
              ((edgeThreshold - distanceToBottom) / edgeThreshold) * maxSpeed;
          }

          scrollSpeedRef.current = { y: newScrollSpeedY };
        }
      }
    };

    // TODO přepsat, volá se moc často
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (startPosition && creatingRef.current) {
        if (!draggingPosition) {
          const vx = Math.abs(event.clientX - creatingRef.current?.clientX);
          const vy = Math.abs(event.clientY - creatingRef.current?.clientY);
          const mouseMoveVectorLength = Math.sqrt(vx * vx + vy * vy);

          if (mouseMoveVectorLength > MOUSE_MOVE_VECTOR) {
            setDraggingPosition(startPosition);
            if (selectedEvent) {
              setSelectedEvent(null);
            }
          }
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [creatingRef.current, startPosition, draggingPosition, selectedEvent]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        (resizingRef.current || creatingRef.current || draggingRef.current) &&
        bodyRef.current &&
        scrollSpeedRef.current &&
        scrollSpeedRef.current?.y !== 0
      ) {
        bodyRef.current.scrollTop += scrollSpeedRef.current.y;
      }
    };

    const intervalId = setInterval(handleScroll, 16); // 60 FPS

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    // Add event listener when the component mounts
    document.addEventListener('mouseup', handleMouseUpOutside);
    document.addEventListener('keydown', handleEscapeKey);

    // Remove event listener when the component unmounts
    return () => {
      document.removeEventListener('mouseup', handleMouseUpOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const setToday = () => {
    if (setDays) {
      setControlState(CalendarDaysInterval.DAY);
      setDays([new Date()]);
    }
  };
  const setThisWeek = () => {
    if (setDays) {
      setControlState(CalendarDaysInterval.WEEK);
      const today = new Date();
      const firstDay = startOfWeek(today, { weekStartsOn: 1 });
      const days = Array.from({ length: 7 }, (_, i) => addDays(firstDay, i));
      setDays(days);
    }
  };
  const isAfterMidnight = (startDate: Date, endDate: Date): boolean => {
    const nextDayMidnight = startOfDay(addDays(startDate, 1));

    // Check if endDate is after next day's midnight
    return isAfter(endDate, nextDayMidnight);
  };

  const setNextWeek = () => {
    if (setDays) {
      switch (controlState) {
        case CalendarDaysInterval.DAY:
          const nextDay = new Date(realDays[0]);
          nextDay.setDate(realDays[0].getDate() + 1);
          setDays([nextDay]);
          break;
        case CalendarDaysInterval.WEEK:
          const firstDayOfCurrentWeek = startOfWeek(realDays[0], {
            weekStartsOn: 1,
          });
          const firstDayOfNextWeek = addDays(firstDayOfCurrentWeek, 7);
          const newDays = Array.from({ length: 7 }, (_, i) =>
            addDays(firstDayOfNextWeek, i)
          );
          setDays(newDays);
          onWeekChange?.(newDays);
          break;
      }
    }
  };

  const setCurrentWeek = () => {
    if (setDays) {
      switch (controlState) {
        case CalendarDaysInterval.DAY:
          setDays([new Date()]);
          break;
        case CalendarDaysInterval.WEEK:
          const today = new Date();
          const firstDay = startOfWeek(today, { weekStartsOn: 1 });
          const days = Array.from({ length: 7 }, (_, i) =>
            addDays(firstDay, i)
          );
          setDays(days);
          onWeekChange?.(days);
          break;
      }
    }
  };

  const setPreviousWeek = () => {
    if (setDays) {
      switch (controlState) {
        case CalendarDaysInterval.DAY:
          const previousDay = new Date(realDays[0]);
          previousDay.setDate(realDays[0].getDate() - 1);
          setDays([previousDay]);
          break;
        case CalendarDaysInterval.WEEK:
          const firstDayOfCurrentWeek = startOfWeek(realDays[0], {
            weekStartsOn: 1,
          });
          const firstDayOfPreviousWeek = addDays(firstDayOfCurrentWeek, -7);
          const newDays = Array.from({ length: 7 }, (_, i) =>
            addDays(firstDayOfPreviousWeek, i)
          );
          setDays(newDays);
          onWeekChange?.(newDays);
          break;
      }
    }
  };

  const handleMouseUp = (
    itemIndex: number,
    itemPartIndex: number,
    clientX: number,
    clientY: number
  ) => {
    if (!startPosition) {
      return;
    }

    const endPosition =
      resisingEvent?.top && draggingPosition
        ? draggingPosition
        : calculatePosition(
            startPosition.columnIndex,
            itemIndex,
            itemPartIndex,
            itemRowsCount
          );

    //otočení start a end position pokud je start position větší než end position
    let localStartPosition =
      startPosition.rowIndex < endPosition.rowIndex
        ? startPosition
        : endPosition;
    let localEndPosition =
      startPosition.rowIndex < endPosition.rowIndex
        ? endPosition
        : startPosition;

    const dates = calculateStartEndDates(
      localStartPosition,
      localEndPosition,
      true
    );
    if (resisingEvent) {
      const originalEvent = events?.find(
        (event) => event.id === resisingEvent.event.id
      );
      const newEvent: EventProps<ID> = {
        ...originalEvent,
        startDate: dates.startDate,
        endDate: dates.endDate,
      };
      onChangeEvent?.(newEvent);
    } else {
      if (creatingRef.current) {
        // JEN když není něco označené, jinak kliknutí je odznačení položky - pokud by ale uživatel přímo jen nekliknul, ale táhnul (klidně o pár pixelů jinam) a chtěl zakládat záznam, tak se jedná o založení
        const vx = Math.abs(clientX - creatingRef.current?.clientX);
        const vy = Math.abs(clientY - creatingRef.current?.clientY);
        const mouseMoveVectorLength = Math.sqrt(vx * vx + vy * vy);

        if (mouseMoveVectorLength > MOUSE_MOVE_VECTOR || !selectedEvent) {
          onCreateEvent?.(dates);
        }
        if (selectedEvent) {
          setSelectedEvent(null);
        }
      }
    }
    //setEvents([...events, [startPosition, endPosition]]);
    setResisingEvent(undefined);
    setStartPosition(undefined);
    setDraggingPosition(undefined);
    resizingRef.current = false;
    creatingRef.current = undefined;
  };

  const calculateStartEndDates = (
    startPosition: EventPosition,
    endPosition: EventPosition,
    includePlusOne = true
  ): EventProps<ID> => {
    const startDay = realDays[startPosition.columnIndex];
    const startHour = Math.floor(startPosition.rowIndex / itemRowsCount);
    const startMinute =
      (startPosition.rowIndex % itemRowsCount) * (60 / itemRowsCount);
    const startDate = new Date(
      startDay.getFullYear(),
      startDay.getMonth(),
      startDay.getDate(),
      startHour,
      startMinute,
      0
    );

    //snad je tohle dobře... idk
    const adjustedEndPositionRow = includePlusOne
      ? endPosition.rowIndex + 1
      : endPosition.rowIndex;
    const endDay = realDays[endPosition.columnIndex];
    const endHour = Math.floor(adjustedEndPositionRow / itemRowsCount);
    const endMinute =
      (adjustedEndPositionRow % itemRowsCount) * (60 / itemRowsCount);
    const endDate = new Date(
      endDay.getFullYear(),
      endDay.getMonth(),
      endDay.getDate(),
      endHour,
      endMinute,
      0
    );
    return { startDate, endDate };
  };

  const calculateStartDate = (startPosition: EventPosition): Date => {
    const startDay = realDays[startPosition.columnIndex];
    const startHour = Math.floor(startPosition.rowIndex / itemRowsCount);
    const startMinute =
      (startPosition.rowIndex % itemRowsCount) * (60 / itemRowsCount);
    const startDate = new Date(
      startDay.getFullYear(),
      startDay.getMonth(),
      startDay.getDate(),
      startHour,
      startMinute,
      0
    );

    return startDate;
  };

  /**
   * Vytváření nové eventu nebo resize eventu.
   */
  const handleMouseEnter = useCallback(
    (itemIndex: number, itemPartIndex: number) => {
      if (!startPosition) {
        return;
      }

      let newPosition = calculatePosition(
        startPosition.columnIndex,
        itemIndex,
        itemPartIndex,
        itemRowsCount
      );

      if (resisingEvent?.top) {
        if (
          !startPosition ||
          startPosition.rowIndex !== newPosition.rowIndex ||
          startPosition.columnIndex !== newPosition.columnIndex
        ) {
          setStartPosition(newPosition);
        }
      } else {
        if (
          !draggingPosition ||
          draggingPosition.rowIndex !== newPosition.rowIndex ||
          draggingPosition.columnIndex !== newPosition.columnIndex
        ) {
          setDraggingPosition(newPosition);
        }
      }
    },
    [startPosition, draggingPosition, moveEventData, resisingEvent]
  );

  const tempEventPosition: EventSimpleProps<ID> | null = useMemo(() => {
    if (!startPosition || !draggingPosition) {
      return null;
    }

    //otočení start a end position pokud je start position větší než end position
    let localStartPosition =
      startPosition.rowIndex < draggingPosition.rowIndex
        ? startPosition
        : draggingPosition;
    let localDraggingPosition =
      startPosition.rowIndex < draggingPosition.rowIndex
        ? draggingPosition
        : startPosition;
    const calculatedOffset = localStartPosition.rowIndex * itemPartHeight;
    const calculatedHeight = resisingEvent
      ? (localDraggingPosition.rowIndex + 1 - localStartPosition.rowIndex) *
        itemPartHeight
      : (localDraggingPosition.rowIndex + 1 - localStartPosition.rowIndex) *
        itemPartHeight;

    return {
      columnIndex: localStartPosition.columnIndex,
      topOffset: calculatedOffset,
      height: calculatedHeight,
      title: 'temp',
      calendarIndex: 0,
      originalEvent: undefined,
    };
  }, [startPosition, draggingPosition]);

  const handleMouseUpOutside = (event: MouseEvent) => {
    if (bodyRef.current && !bodyRef.current.contains(event.target as Node)) {
      handleStopAction();
    }
  };

  const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleStopAction();
    }
  };

  const handleStopAction = () => {
    setStartPosition(undefined);
    setDraggingPosition(undefined);
    setResisingEvent(undefined);
    setMoveEventData(null);
    document.body.style.cursor = 'default';
    resizingRef.current = false;
    creatingRef.current = undefined;
  };

  /**
   * Přesun eventu kdy se chytne myší a přesouvá po kalendáři.
   * @param columnIndex
   * @param itemIndex
   * @param partIndex
   */
  const handleMoveDragOver = (
    columnIndex: number,
    /**
     * Index hodiny.
     */
    itemIndex: number,
    /**
     * Index v hodině.
     */
    partIndex: number
  ) => {
    if (!moveEventData) {
      return;
    }

    const highestEndPoistion = hours * itemRowsCount;
    const endPosition = moveEventData.height / itemPartHeight;
    let fakePosition =
      itemIndex * itemRowsCount + partIndex - moveEventData.cursorIndex;
    if (fakePosition >= 0) {
      if (fakePosition + endPosition <= highestEndPoistion) {
        if (
          moveEventData.topIndex !== fakePosition ||
          moveEventData.columnIndex !== columnIndex
        ) {
          setMoveEventData({
            ...moveEventData,
            topIndex: fakePosition,
            columnIndex: columnIndex,
          });
        }
      } else {
        if (
          moveEventData.topIndex !== highestEndPoistion - endPosition ||
          moveEventData.columnIndex !== columnIndex
        ) {
          setMoveEventData({
            ...moveEventData,
            topIndex: highestEndPoistion - endPosition,
            columnIndex: columnIndex,
          });
        }
      }
    }
  };

  const handleDragStart = (
    e: DragEvent<HTMLDivElement>,
    event: EventSimpleProps<ID>
  ) => {
    if (event.id) {
      e.preventDefault();

      const cloneEvent = e.ctrlKey || e.metaKey;

      const target = e.currentTarget as HTMLDivElement;
      const boundingRect = target.getBoundingClientRect();
      const yClickPosiiton = e.clientY - boundingRect.top;
      const itemIndex = Math.floor(yClickPosiiton / itemPartHeight);
      let timeLength = 0;
      if (event.dateRange) {
        timeLength = differenceInMilliseconds(
          event.dateRange[1],
          event.dateRange[0]
        );
      }

      setMoveEventData({
        clone: cloneEvent,
        cursorIndex: itemIndex,
        height: event.height,
        id: event.id,
        columnIndex: event.columnIndex,
        timeLength: timeLength,
        topIndex: Math.floor(event.topOffset / itemPartHeight),
        currentDraggingEvent: event,
      });
      draggingRef.current = true;
    }
  };

  const getEventById = (id: ID) => {
    if (!simpleEvents) {
      return undefined;
    }
    return events?.find((event) => event.id === id);
  };

  const handleDrop = (
    e: React.MouseEvent<HTMLDivElement>,
    columnIndex: number
  ) => {
    e.preventDefault();

    if (!moveEventData) {
      return;
    }

    // Calculate the Y offset from the top of the container, adjusted for the initial drag offset
    const itemIndex = Math.floor(moveEventData.topIndex / itemRowsCount);
    const partIndex = moveEventData.topIndex % itemRowsCount;

    const startPosition = calculatePosition(
      columnIndex,
      itemIndex,
      partIndex,
      itemRowsCount
    );
    const originalEvent = getEventById(moveEventData.id);
    const startDate = calculateStartDate(startPosition);
    const endDate = new Date(startDate.getTime() + moveEventData.timeLength);
    if (!isAfterMidnight(startDate, endDate)) {
      // Změna eventu nebo založení nového eventu klonováním
      if (moveEventData.clone) {
        if (originalEvent?.id !== undefined) {
          onCreateEvent?.(
            { ...originalEvent, id: undefined, startDate, endDate },
            // @ts-ignore
            originalEvent.id
          );
        }
      } else {
        onChangeEvent?.({ ...originalEvent, startDate, endDate });
      }
    }
    setMoveEventData(null);
    draggingRef.current = false;
  };

  const handleResizeDragStart = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    top: boolean,
    event: EventSimpleProps<ID>
  ) => {
    const offsetY = event.topOffset;
    e.preventDefault();
    e.stopPropagation();

    const startItemIndex = Math.floor(offsetY / calendarItemHeight);
    const startPartIndex = Math.floor(
      (offsetY % calendarItemHeight) / (calendarItemHeight / itemRowsCount)
    );

    const pulkaMalehoRadku = calendarItemHeight / itemRowsCount;

    const endItemIndex = Math.floor(
      (offsetY + event.height - pulkaMalehoRadku) / calendarItemHeight
    );
    const endPartIndex = Math.floor(
      ((offsetY + event.height - pulkaMalehoRadku) % calendarItemHeight) / // pizxel dolu aby se vešel do správné řádky
        (calendarItemHeight / itemRowsCount)
    );

    const startingPosition = calculatePosition(
      event.columnIndex,
      startItemIndex,
      startPartIndex,
      itemRowsCount
    );
    const endingPosition = calculatePosition(
      event.columnIndex,
      endItemIndex,
      endPartIndex,
      itemRowsCount
    );

    setStartPosition(startingPosition);
    setDraggingPosition(endingPosition);
    setResisingEvent({ top, event });
    resizingRef.current = true;
  };

  // Varianty stavů eventů - zakládání, změna, přehování apod.
  const isMovingOrCloning = !!moveEventData;
  const isResizing = draggingPosition && tempEventPosition && resisingEvent;
  const isCreating = draggingPosition && tempEventPosition && !resisingEvent;

  return (
    <div ref={calendarRef} className={Classes.CALENDAR}>
      <CalendarFixedPlusButton onClick={onCreateEvent} />

      <div className={Classes.CALENDAR_HEADER}>
        <div className={Classes.CALENDAR_HEADER_TITLE_ACTIONS}>
          <div
            className={classNames(
              'w-100 d-flex align-items-center',
              Classes.CALENDAR_TITLE
            )}
          >
            {showDate}
          </div>
          <ButtonToolbar className={'justify-content-end w-100'}>
            {setDays && (
              <>
                <Button
                  onClick={setToday}
                  style={
                    controlState === CalendarDaysInterval.DAY
                      ? 'default'
                      : 'link'
                  }
                  icon={<Icon icon={mcDay} />}
                >
                  Den
                </Button>
                <Button
                  onClick={setThisWeek}
                  style={
                    controlState === CalendarDaysInterval.WEEK
                      ? 'default'
                      : 'link'
                  }
                  icon={<Icon icon={mcWeek} />}
                >
                  Týden
                </Button>
                <div className={'d-flex align-items-center'}>
                  <Button
                    disabled={!controlState}
                    onClick={setPreviousWeek}
                    style={'link'}
                    icon={<Icon icon={mcNavigatePrev} />}
                    title={
                      controlState === CalendarDaysInterval.WEEK
                        ? 'Předchozí týden'
                        : 'Předchozí den'
                    }
                  />
                  <Button
                    disabled={!controlState}
                    onClick={setCurrentWeek}
                    style={'link'}
                    title={
                      controlState === CalendarDaysInterval.WEEK
                        ? 'Aktuální týden'
                        : 'Aktuální den'
                    }
                  >
                    dnes
                  </Button>
                  <Button
                    disabled={!controlState}
                    onClick={setNextWeek}
                    style={'link'}
                    icon={<Icon icon={mcNavigateNext} />}
                    title={
                      controlState === CalendarDaysInterval.WEEK
                        ? 'Následující týden'
                        : 'Následující den'
                    }
                  />
                </div>
              </>
            )}
            <Button
              menu={
                <Menu>
                  <MenuDivider title={'Nastavení hodin'} />
                  {timeIntervals.map((interval) => (
                    <MenuItem
                      active={selectedTimeInterval === interval}
                      onClick={() => setSelectedTimeInterval(interval)}
                      text={`${interval} minut`}
                    />
                  ))}
                </Menu>
              }
              style={'link'}
              icon={<Icon icon={mcSettings} />}
            />
          </ButtonToolbar>
        </div>
        <div className={Classes.CALENDAR_HEADER_DAY_TITLE_COLUMNS}>
          <div
            style={{
              width: hoursListColumnWidth,
              minWidth: hoursListColumnWidth,
            }}
          ></div>
          {realDays.map((day) => {
            const dayName = format(day, 'EEEEEE', { locale: locale });
            const active = isSameDay(day, new Date());
            return (
              <div
                className={classNames(
                  Classes.CALENDAR_HEADER_DAY_TITLE_COLUMN,
                  { [Classes.CALENDAR_HEADER_DAY_TITLE_COLUMN_ACTIVE]: active }
                )}
                title={format(day, 'PPPP', { locale })}
              >
                <div className={Classes.CALENDAR_DAY_TITLE}>
                  {format(day, 'dd', { locale: locale })}
                </div>
                <div className={Classes.CALENDAR_WEEK_DAY_TITLE}>
                  {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                </div>
                {dayHeaderExtra && (
                  <div className={Classes.CALENDAR_DAY_EXTRA}>
                    {dayHeaderExtra({ date: day })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className={Classes.CALENDAR_HEADER_DAY_EVENT_COLUMNS}>
          <div style={{ width: hoursListColumnWidth }}></div>
          {realDays.map((today) => (
            <div className={Classes.CALENDAR_HEADER_DAY_EVENT_COLUMN}>
              {fullDayEvents
                ?.filter((fullDayEvent) => {
                  const startDate = startOfDay(
                    new Date(fullDayEvent.startDate)
                  );
                  const endDate = endOfDay(new Date(fullDayEvent.endDate));
                  return isWithinInterval(today, {
                    start: startDate,
                    end: endDate,
                  });
                })
                .map((event) => {
                  const calendarIndex =
                    calendarEvents?.findIndex((e) =>
                      e.events.some((ev) => ev.id === event.id)
                    ) || 0;
                  return (
                    <FullDayEvent<ID>
                      locale={locale}
                      onClick={() => {
                        setSelectedEvent(event);
                      }}
                      selected={selectedEvent?.id === event.id}
                      event={event}
                      onEdit={() => onEditEvent?.(event)}
                      onDelete={() => onDeleteEvent?.(event)}
                      colorIndex={calendarIndex}
                      customEventTooltip={customEventTooltip}
                    />
                  );
                })}
            </div>
          ))}
        </div>
      </div>
      <div ref={bodyRef} className={Classes.CALENDAR_BODY} tabIndex={-1}>
        <div
          style={{
            left: hoursListColumnWidth,
            width: timeLineData.width,
            top: timeLineTopOffset,
          }}
          className={Classes.CALENDAR_TIME_LINE}
        />
        {timeLineData.showCurrentDay && (
          <div
            style={{
              left: hoursListColumnWidth + timeLineData.currentDayLeft,
              width: timeLineData.currentDayWidth,
              top: timeLineTopOffset,
            }}
            className={classNames(
              Classes.CALENDAR_TIME_LINE,
              Classes.CALENDAR_TIME_LINE_CURRENT_DAY
            )}
          />
        )}
        <div
          style={{
            width: hoursListColumnWidth,
            paddingTop: calendarItemHeight - HOUR_COLUMN_ITEM_LINE_HEIGHT / 2,
            gap: calendarItemHeight - HOUR_COLUMN_ITEM_LINE_HEIGHT,
          }}
          className={Classes.CALENDAR_HOURS_LIST_COLUMN}
        >
          {hoursArray.slice(0, -1).map((val) => (
            <div
              className={Classes.CALENDAR_HOURS_LIST_ITEM}
              style={{ lineHeight: `${HOUR_COLUMN_ITEM_LINE_HEIGHT}px` }}
            >
              {val + 1}
            </div>
          ))}
        </div>
        {colsArray.map((_, colIndex) => (
          <div
            onMouseUp={(e) => isMovingOrCloning && handleDrop(e, colIndex)}
            onDragOver={(e) => e.preventDefault()}
            className={classNames(Classes.CALENDAR_COLUMN, {
              [Classes.CALENDAR_WEEKEND_COLUMN]:
                isSaturday(realDays[colIndex]) || isSunday(realDays[colIndex]),
            })}
          >
            {simpleEvents?.[colIndex]?.map((event) => {
              const hideEvent = resisingEvent?.event?.id === event.id;
              const ee = events.find((e) => e.id === event.id);
              return (
                <CalendarEvent<ID>
                  locale={locale}
                  selected={selectedEvent?.id === event.id}
                  columnContentWidth={columnContentWidth}
                  onClick={() => {
                    ee && setSelectedEvent(ee);
                  }}
                  colorIndex={event.calendarIndex}
                  onEdit={() => {
                    !hideEvent && ee && onEditEvent?.(ee);
                  }}
                  onDelete={() => {
                    !hideEvent && ee && onDeleteEvent?.(ee);
                  }}
                  originalPosition={event.id === moveEventData?.id || hideEvent}
                  pointerEventsNone={
                    !!startPosition || isMovingOrCloning || hideEvent
                  }
                  handleResizeDragStart={
                    !hideEvent ? handleResizeDragStart : undefined
                  }
                  event={event}
                  handleDragStart={!hideEvent ? handleDragStart : undefined}
                  customEventTooltip={customEventTooltip}
                />
              );
            })}
            {hoursArray.map((_, itemIndex) => (
              <div
                style={{
                  height: calendarItemHeight,
                  minHeight: calendarItemHeight,
                }}
                className={Classes.CALENDAR_ITEM}
                onDragStart={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {rowsArray.map((_, partIndex) => (
                  <div
                    className={Classes.CALENDAR_ITEM_PART}
                    onMouseDown={(e) => {
                      if (e.button === 0) {
                        let position = calculatePosition(
                          colIndex,
                          itemIndex,
                          partIndex,
                          itemRowsCount
                        );
                        setStartPosition(position);
                        creatingRef.current = {
                          clientX: e.clientX,
                          clientY: e.clientY,
                        };
                      }
                    }}
                    onDragStart={(e) => {
                      //nemám nejmenší tušení proč je tohle potřeba, ale mouseDown zde občas vyvolá drag event,
                      // takže je potřeba ho ignorovat, TODO Stánek, napiš kdybys na to v budoucnu přišel
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseUp={(e) => {
                      if (e.button === 0) {
                        if (startPosition) {
                          handleMouseUp(
                            itemIndex,
                            partIndex,
                            e.clientX,
                            e.clientY
                          );
                        }
                      }
                    }}
                    onMouseEnter={() => {
                      if (moveEventData) {
                        handleMoveDragOver(colIndex, itemIndex, partIndex);
                      } else {
                        if (startPosition) {
                          handleMouseEnter(itemIndex, partIndex);
                        }
                      }
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
        {isCreating && (
          <div
            style={{
              left: calculateLeftPosition(
                tempEventPosition.columnIndex,
                bodyClientWidth - hoursListColumnWidth,
                columnsCount,
                hoursListColumnWidth
              ),
              top: tempEventPosition.topOffset + EVENT_VERTICAL_MARGIN,
              height:
                tempEventPosition.height -
                EVENT_VERTICAL_MARGIN -
                EVENT_VERTICAL_MARGIN,
              width:
                (bodyClientWidth - hoursListColumnWidth) / columnsCount -
                RIGHT_COLUMN_SPACE -
                4, // ?????????????
            }}
            className={Classes.CALENDAR_EVENT_TEMP}
          />
        )}
        {isResizing && (
          <div
            style={{
              left:
                calculateLeftPosition(
                  tempEventPosition.columnIndex,
                  bodyClientWidth - hoursListColumnWidth,
                  columnsCount,
                  hoursListColumnWidth
                ) + columnBorder,
              top: tempEventPosition.topOffset + EVENT_VERTICAL_MARGIN,
              height:
                tempEventPosition.height -
                EVENT_VERTICAL_MARGIN -
                EVENT_VERTICAL_MARGIN,
              width:
                (bodyClientWidth - hoursListColumnWidth) / columnsCount -
                RIGHT_COLUMN_SPACE -
                4, // ????????????????
            }}
            className={Classes.CALENDAR_EVENT_DRAGGING}
          >
            <CalendarEvent<ID>
              locale={locale}
              columnContentWidth={columnContentWidth}
              colorIndex={resisingEvent.event.calendarIndex}
              inline
              active
              originalPosition={false}
              pointerEventsNone={true}
              event={resisingEvent.event}
              customEventTooltip={customEventTooltip}
            />
          </div>
        )}
        {isMovingOrCloning && (
          <div
            style={{
              left:
                calculateLeftPosition(
                  moveEventData.columnIndex,
                  bodyClientWidth - hoursListColumnWidth,
                  columnsCount,
                  hoursListColumnWidth
                ) + columnBorder,
              top:
                moveEventData.topIndex * itemPartHeight + EVENT_VERTICAL_MARGIN,
              height:
                moveEventData.height -
                EVENT_VERTICAL_MARGIN -
                EVENT_VERTICAL_MARGIN,
              width:
                (bodyClientWidth - hoursListColumnWidth) / columnsCount -
                RIGHT_COLUMN_SPACE -
                4, // ????????????????
              cursor: 'copy',
            }}
            className={Classes.CALENDAR_EVENT_DRAGGING}
          >
            <CalendarEvent<ID>
              cloning={moveEventData?.clone}
              locale={locale}
              columnContentWidth={columnContentWidth}
              colorIndex={moveEventData.currentDraggingEvent.calendarIndex}
              inline
              active
              originalPosition={false}
              pointerEventsNone={true}
              event={moveEventData.currentDraggingEvent}
              customEventTooltip={customEventTooltip}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const countOverlappingDatesFc = <ID extends unknown>(
  currentEvent: EventProps<ID>,
  dateArray: EventProps<ID>[]
): OverlappingStatus => {
  const startDate = currentEvent.startDate;
  const endDate = currentEvent.endDate;
  // Create the interval object for the given start and end dates
  const interval = { start: startDate, end: endDate };

  // Filter the array to find overlapping intervals
  const overlappingDates = dateArray.filter((obj) => {
    const objInterval = { start: obj.startDate, end: obj.endDate };
    return areIntervalsOverlapping(interval, objInterval);
  });

  // Return the count of overlapping intervals
  const overLappedIndex = overlappingDates.findIndex(
    (item) => item.id === currentEvent.id
  );

  return { overLappedIndex, overlappingDates: overlappingDates.length };
};
